/**
 * Role-based permission utilities
 * Defines which roles can perform which actions
 */

import i18n from '@/i18n/i18n';
import { getRoleLabel } from '@/i18n/labelMaps';
import { peopleApi } from '@/api/peopleWrapper';
import { familyHasPaidPlan } from '@/utils/familyPlan';
import type { MemberAccessMode, RoleExplanationResponse } from 'we-tree-api-client/v2';

export type UserRole = 'owner' | 'admin' | 'editor' | 'contributor' | 'viewer';
export type { MemberAccessMode };

export interface MemberAccessState {
  effectiveRole: string | null;
  accessMode: MemberAccessMode;
  canDirectModify: boolean;
  canSuggest: boolean;
  canReview: boolean;
  canManageAccess: boolean;
}

const EMPTY_MEMBER_ACCESS: MemberAccessState = {
  effectiveRole: null,
  accessMode: 'view',
  canDirectModify: false,
  canSuggest: false,
  canReview: false,
  canManageAccess: false
};

const ROLE_RANK: Record<UserRole, number> = {
  owner: 5,
  admin: 4,
  editor: 3,
  contributor: 2,
  viewer: 1
};

export function getRoleRank(role: UserRole | string | null | undefined): number {
  if (!role) return 0;
  return ROLE_RANK[role as UserRole] || 0;
}

/**
 * Roles the actor may assign (equal or lesser than their own).
 */
export function getAssignableRoles(actorRole: UserRole | string | null | undefined): UserRole[] {
  const rank = getRoleRank(actorRole);
  return (Object.keys(ROLE_RANK) as UserRole[])
    .filter((r) => ROLE_RANK[r] <= rank)
    .sort((a, b) => ROLE_RANK[b] - ROLE_RANK[a]);
}

/**
 * Free: owner/admin only. Paid/gifted: any active member role.
 */
export function canInvite(role: UserRole | string | null | undefined, plan?: string | null): boolean {
  if (!role) return false;
  if (role === 'owner' || role === 'admin') return true;
  return familyHasPaidPlan(plan);
}

/**
 * Same tier gate as invites — change roles of equal-or-lesser collaborators.
 */
export function canChangeRoles(role: UserRole | string | null | undefined, plan?: string | null): boolean {
  return canInvite(role, plan);
}

/**
 * Whether actor may edit target's current role (equal-or-lesser current role; not self).
 */
export function canChangeUserRole(
  actorRole: UserRole | string | null | undefined,
  targetRole: UserRole | string | null | undefined,
  actorUserId: string | null | undefined,
  targetUserId: string | null | undefined,
  plan?: string | null
): boolean {
  if (!canChangeRoles(actorRole, plan)) return false;
  if (!actorUserId || !targetUserId || actorUserId === targetUserId) return false;
  return getRoleRank(targetRole) > 0 && getRoleRank(targetRole) <= getRoleRank(actorRole);
}

/**
 * Creating member blocks requires Family plan entitlements (API feature gate on create).
 */
export function canCreateMemberBlocks(plan: string | null | undefined): boolean {
  return familyHasPaidPlan(plan);
}

/**
 * Check if a user role can upload GEDCOM files
 * Only owners and admins can upload
 */
export function canUploadGedcom(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === 'owner' || role === 'admin';
}

/**
 * Check if a user role can export GEDCOM (full or delta).
 * Owners and admins only — free on all tiers.
 */
export function canExportGedcom(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === 'owner' || role === 'admin';
}

/**
 * Check if a user role can view detailed user metadata (nickname, email)
 * Only owners and admins can view this PII
 */
export function canViewUserDetails(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === 'owner' || role === 'admin';
}

/**
 * Build member access state from role-explanation API response.
 */
export function memberAccessFromRoleExplanation(data: RoleExplanationResponse | null | undefined): MemberAccessState {
  if (!data) return EMPTY_MEMBER_ACCESS;

  return {
    effectiveRole: data.effectiveRole ?? null,
    accessMode: data.accessMode,
    canDirectModify: data.canDirectModify,
    canSuggest: data.canSuggest,
    canReview: data.canReview,
    // Managing access is relationship-scoped, so the effective role alone does not decide it: a
    // family admin who has not claimed a profile resolves to admin here yet may not change anyone
    // else's privacy. Trust the API rather than re-deriving, or the UI offers a button that 403s.
    canManageAccess: (data as { canManageAccess?: boolean }).canManageAccess === true
  };
}

/**
 * Per-member direct modify (Edit profile, facts, life events).
 * Prefer memberAccessFromRoleExplanation from role-explanation API.
 */
export function canDirectModifyMember(canModifyFromApi: boolean): boolean {
  return canModifyFromApi;
}

export function getMemberAccessMode(
  canModifyFromApi: boolean,
  effectiveRole: string | null | undefined
): MemberAccessMode {
  if (canModifyFromApi) return 'edit';
  if (canSuggestChangesForMember(canModifyFromApi, effectiveRole)) return 'suggest';
  return 'view';
}

export function getMemberAccessPrefix(mode: MemberAccessMode): string {
  if (mode === 'edit') return i18n.t('forms:accessPrefix.editing');
  if (mode === 'suggest') return i18n.t('forms:accessPrefix.contributing');
  return i18n.t('forms:accessPrefix.viewing');
}

export function getMemberAccessLabel(mode: MemberAccessMode, effectiveRole: string | null | undefined): string {
  return `${getMemberAccessPrefix(mode)} ${getRoleDisplayName(effectiveRole)}`;
}

/**
 * Suggest changes workflow (profile, facts, life events) when direct edit is not allowed.
 */
export function canSuggestChangesForMember(
  canModifyFromApi: boolean,
  effectiveRole: string | null | undefined
): boolean {
  if (canModifyFromApi) return false;
  if (!effectiveRole || effectiveRole === 'viewer') return false;
  return effectiveRole === 'contributor' || effectiveRole === 'editor';
}

/**
 * Check if a user role can modify family data (family-level operations, not per-member direct edit).
 */
export function canModifyFamily(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return ['owner', 'admin', 'editor', 'contributor'].includes(role);
}

/**
 * Check if a user role can delete media assets (media:delete capability).
 * Owners, admins, and editors — not contributors or viewers.
 */
export function canDeleteMedia(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === 'owner' || role === 'admin' || role === 'editor';
}

/**
 * Check if a user role can request a bulk media ZIP export (billing §5.7 carve-out).
 * Owners and admins only — full-library egress is sensitive.
 */
export function canExportMedia(role: UserRole | string | null | undefined): boolean {
  if (!role) return false;
  return role === 'owner' || role === 'admin';
}

/**
 * Get a human-readable role name
 */
export function getRoleDisplayName(role: UserRole | string | null | undefined): string {
  if (!role) return i18n.t('common:unknown');
  const roleKey = role as UserRole;
  if (
    roleKey === 'owner' ||
    roleKey === 'admin' ||
    roleKey === 'editor' ||
    roleKey === 'contributor' ||
    roleKey === 'viewer'
  ) {
    return getRoleLabel(roleKey);
  }
  return role;
}

/**
 * Check if the user can modify a specific family member
 * This checks member-level permissions, not just family-level
 * @param familyMemberId The family member ID to check
 * @returns Promise resolving to true if user can modify, false otherwise
 */
export async function canModifyFamilyMember(familyMemberId: string): Promise<boolean> {
  try {
    const result = await peopleApi.canModifyMember(familyMemberId);
    return result.success && result.data === true;
  } catch {
    return false;
  }
}

/**
 * Batch check if the user can modify multiple family members.
 * Use this when displaying a list/group of members to reduce API calls.
 * @param familyMemberIds Array of family member IDs to check
 * @returns Promise resolving to a map of familyMemberId -> canModify
 */
export async function canModifyFamilyMembers(familyMemberIds: string[]): Promise<Record<string, boolean>> {
  if (familyMemberIds.length === 0) return {};
  try {
    const result = await peopleApi.canModifyMembers(familyMemberIds);
    return result.success ? result.data : {};
  } catch {
    return {};
  }
}
